import AddLink from "controls/global/add-link";
import React,
{
  useEffect,
  useState,
} from "react";
import {
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { gaps } from "theme/defaultStyle";
import { useLookup } from "utils/context/LookupContext";
import { useParty } from "utils/context/PartyContext";
import { usePartySellerActions } from "utils/context/PartySellerContext";
import { usePartyTypes } from "utils/context/PartyTypesContext";
import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { getDefaultSellerObject } from "utils/data/parties";
import { v4 as uuidv4 } from "uuid";
import SellerData from "./SellerData";
import { PartyRoleTypeName } from "utils/data/enum";
import useFocus from "utils/custom-hooks/useFocus";
import {
  Box,
  styled,
} from "@mui/material";
import ConfirmationDialogV3 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV3";

const formPrefix = "sellerParties";

const StyledGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
});

const SellerCollection = () => {
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [partyIndex, setPartyIndex] = useState<number>(0);
  const [deletePartyIndex, setDeletePartyIndex] = useState(-1);
  const { setValue, getValues } = useFormWrapper();
  const [, { setSellers }] = usePartySellerActions();
  const watchedFields = useWatch({
    name: [
      "fileNameNumber",
      "agency",
      "properties.0",
      "additionalParties",
      "hasIssuedProducts",
      "isPopulatingExistingFile",
    ],
  });
  const fileNameNumber = watchedFields[0];
  const agency = watchedFields[1];
  const primaryProperty = watchedFields[2];
  const additionalParties = watchedFields[3];
  const hasIssuedProducts = watchedFields[4];
  const isPopulatingExistingFile = watchedFields[5];
  const { fields, append, remove } = useFieldArray({
    name: formPrefix,
    keyName: "fieldId",
  });
  const isFileDisabled = useFieldDisabler("SellerClearButton");
  const isAddSellerDisabled = useFieldDisabler("AddSellerButton");
  const [, { deleteParty }] = useParty();
  const [{ suffixes }] = useLookup();
  const [{ basicPartyTypes }, { getPartyTypes }] = usePartyTypes();
  const { setFocusInputElement } = useFocus();

  const handleAddItem = (e: any) => {
    e.preventDefault();
    setPartyIndex(partyIndex + 1);
    let filePartyKey = uuidv4();
    const fileParty = getDefaultSellerObject();
    fileParty.filePartyKey = filePartyKey;
    fileParty.integrationKey = filePartyKey;
    fileParty.isDefault = false;
    fileParty.isDirty = true;
    append(fileParty);

    //once you append a new party, the first is no longer default
    setValue(`${formPrefix}.0.isDefault`, false);
    setValue(`${formPrefix}.0.isDirty`, true);

    // set focus to last Seller Parties
    const newItemIndex = fields.length;
    setFocusInputElement(`sellerParties.${newItemIndex}.partyTypeCode`);
  };

  const deleteSeller = (index: number) => {
    setOpenConfirmationDialog(true);
    setDeletePartyIndex(index);
  };

  const handleYes = (e: any) => {
    if (deletePartyIndex !== -1) {
      const currentParty = fields[deletePartyIndex] as any;

      // Delete only if fileParty is in db
      if (
        currentParty?.filePartyId &&
        currentParty?.filePartyId > 0 &&
        currentParty?.integrationKey
      )
        deleteParty(currentParty?.integrationKey, agency?.id, fileNameNumber);

      if (partyIndex >= 0) {
        remove(deletePartyIndex);
        setPartyIndex(partyIndex - 1);

        // Update PartySellerContext
        setSellers(getValues("sellerParties"));
      }

      setDeletePartyIndex(-1);
    }
    setOpenConfirmationDialog(false);
  };

  const handleNo = () => {
    setOpenConfirmationDialog(false);
  };

  const handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === "Tab" && !evt.shiftKey && document?.getElementById(PartyRoleTypeName.Lender)) {
      evt.preventDefault();
      document?.getElementById(PartyRoleTypeName.Lender)?.focus();
    }
  }

  useEffect(() => {
    if (basicPartyTypes.length === 0) getPartyTypes();
    if (fields.length > 0) {
      setPartyIndex(fields.length - 1);
    } else {
      const buyerBurrower = getDefaultSellerObject();
      append(buyerBurrower);

      // Update PartySellerContext
      setSellers(getValues("sellerParties"));

    }
  }, [getPartyTypes, fields, setPartyIndex, append]);

  return (
    <>
      <StyledGroup>
        {fields.map((field: any, index: number) => {
          const handleDeleteSeller = () => {
            deleteSeller(index);
          };

          return <SellerData
            key={field.fieldId}
            formPrefix={formPrefix}
            field={field}
            index={index}
            isFileDisabled={isFileDisabled}
            onDeleteSeller={handleDeleteSeller}
            partyTypes={basicPartyTypes}
            suffixes={suffixes}
            fileNameNumber={fileNameNumber}
            agency={agency}
            primaryProperty={primaryProperty}
            additionalParties={additionalParties}
            hasIssuedProducts={hasIssuedProducts}
            isPopulatingExistingFile={isPopulatingExistingFile}
          />;
        })}

        <Box>
          <AddLink
            disabled={isAddSellerDisabled}
            onClick={handleAddItem}
            onKeyDown={handleKeyDown}
          >
            Add Seller
          </AddLink>
        </Box>
      </StyledGroup>

      <ConfirmationDialogV3
        confirmationMessage="Delete this party?"
        isOpen={openConfirmationDialog}
        onYes={handleYes}
        onNo={handleNo}
        onCancel={handleNo}
        cancelActionButton={true}
        singleActionButtonText={"Delete"}
        buttonNoText={"Cancel"}
      />

    </>
  );
};

export default SellerCollection;
